import { useState, useCallback } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import type { ScriptStep, ScriptStepWithId, StepType } from '../types/steps';
import { hydrateStep, stripStepDefaults } from '../types/steps';
import { getStepDefaults } from '../utils/stepDefaults';

export const nextStepId = () => `step-${crypto.randomUUID()}`;

export const toStepsWithId = (steps: ScriptStep[]): ScriptStepWithId[] =>
  steps.map((raw, index) => {
    const hydrated = hydrateStep(raw as unknown as Record<string, unknown>);
    return { ...hydrated, id: nextStepId(), index };
  });

export const reindex = (steps: ScriptStepWithId[]): ScriptStepWithId[] =>
  steps.map((step, index) => ({ ...step, index }));

export const stripForPersistence = (steps: ScriptStepWithId[]): ScriptStep[] =>
  steps.map(
    ({ id: _id, index: _index, ...rest }) =>
      stripStepDefaults(rest as ScriptStep) as ScriptStep
  );

export type UseStepArrayReturn = {
  steps: ScriptStepWithId[];
  addStep: (type: StepType) => void;
  removeStep: (id: string) => void;
  updateStep: (id: string, updates: Partial<ScriptStep>) => void;
  reorderSteps: (activeId: string, overId: string) => void;
  replaceSteps: (steps: ScriptStep[]) => void;
  reset: (steps?: ScriptStep[]) => void;
  toScriptSteps: () => ScriptStep[];
};

/**
 * Reusable controller for a single ordered list of script steps. Owns the
 * id/index bookkeeping (toStepsWithId/reindex) and persistence stripping that
 * StepList requires, so any editor — the Script Builder sections or the robot
 * Master Setup/Teardown lists — can drive a StepList without re-implementing it.
 */
export const useStepArray = (
  initial: ScriptStep[] = []
): UseStepArrayReturn => {
  const [steps, setSteps] = useState<ScriptStepWithId[]>(() =>
    toStepsWithId(initial)
  );

  const addStep = useCallback((type: StepType) => {
    const defaults = getStepDefaults(type);
    setSteps(prev =>
      reindex([
        ...prev,
        {
          ...defaults,
          id: nextStepId(),
          index: prev.length
        } as ScriptStepWithId
      ])
    );
  }, []);

  const removeStep = useCallback((id: string) => {
    setSteps(prev => reindex(prev.filter(s => s.id !== id)));
  }, []);

  const updateStep = useCallback(
    (id: string, updates: Partial<ScriptStep>) => {
      setSteps(prev =>
        prev.map(s =>
          s.id === id ? ({ ...s, ...updates } as ScriptStepWithId) : s
        )
      );
    },
    []
  );

  const reorderSteps = useCallback((activeId: string, overId: string) => {
    setSteps(prev => {
      const oldIndex = prev.findIndex(s => s.id === activeId);
      const newIndex = prev.findIndex(s => s.id === overId);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return reindex(arrayMove(prev, oldIndex, newIndex));
    });
  }, []);

  const replaceSteps = useCallback((newSteps: ScriptStep[]) => {
    setSteps(toStepsWithId(newSteps));
  }, []);

  const reset = useCallback((newSteps: ScriptStep[] = []) => {
    setSteps(toStepsWithId(newSteps));
  }, []);

  const toScriptSteps = useCallback(
    (): ScriptStep[] => stripForPersistence(steps),
    [steps]
  );

  return {
    steps,
    addStep,
    removeStep,
    updateStep,
    reorderSteps,
    replaceSteps,
    reset,
    toScriptSteps
  };
};
